/**
 * Block Key Plugin for ProseMirror
 *
 * Assigns stable unique IDs (blockKey) to paragraph nodes. These IDs are used
 * for tracking document structure and can be referenced by highlights and other
 * features that need to survive document edits.
 *
 * Features:
 * - Auto-generates unique keys for new paragraphs
 * - Preserves existing keys during edits
 * - Handles copy/paste by regenerating keys for pasted content
 * - Works with undo/redo (keys persist through history)
 *
 * Based on: ai_plans/standalone-lift/src/editor/schema.ts (blockKeyPlugin)
 */

import type { Transaction } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";

/**
 * Generate a short unique ID for block keys.
 * Uses base36 encoding for compact representation.
 * Format: 6 characters (e.g., "a1b2c3")
 *
 * Based on: https://stackoverflow.com/questions/6248666/how-to-generate-short-uid-like-ax4j9z-in-js
 */
function generateBlockKey(): string {
  const a = (Math.random() * 46656) | 0;
  const b = (Math.random() * 46656) | 0;
  return ("000" + a.toString(36)).slice(-3) + ("000" + b.toString(36)).slice(-3);
}

export const blockKeyPluginKey = new PluginKey("blockKey");

/**
 * Plugin that ensures all block nodes with a blockKey attribute have unique IDs.
 *
 * The plugin runs after each transaction that changes the document and:
 * 1. Finds all nodes with a blockKey attribute
 * 2. Assigns new keys to nodes with empty or duplicate keys
 * 3. Returns a new transaction with the updated keys
 */
export const blockKeyPlugin = new Plugin({
  key: blockKeyPluginKey,

  appendTransaction(
    transactions: readonly Transaction[],
    _oldState,
    newState
  ): Transaction | null {
    // Only process if the document actually changed
    const docChanged = transactions.some((tr) => tr.docChanged);
    if (!docChanged) {
      return null;
    }

    const tr = newState.tr;
    let modified = false;

    // Track seen keys to detect duplicates
    const seenKeys = new Set<string>();

    // Iterate through all nodes in the document
    newState.doc.descendants((node, pos) => {
      // Check if this node type has a blockKey attribute
      if (!("blockKey" in node.attrs)) {
        return;
      }

      let blockKey = node.attrs.blockKey as string;

      // Generate new key if:
      // - Key is empty or null
      // - Key is a duplicate (already seen)
      if (blockKey === "" || blockKey === null || seenKeys.has(blockKey)) {
        blockKey = generateBlockKey();

        // Update the node's attributes
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockKey });
        modified = true;
      }

      seenKeys.add(blockKey);
    });

    return modified ? tr : null;
  },
});
